// src/components/HeaderNew.js

import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';

const ForzaMark = ({ className = '' }) => (
  <img src="/ForzaIcon.png" alt="" className={`h-6 w-6 ${className}`} />
);

const TrophyIcon = ({ className = '' }) => (
  <svg
    className={`h-6 w-6 ${className}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M8 21h8" />
    <path d="M12 17c2.761 0 5-2.239 5-5V4H7v8c0 2.761 2.239 5 5 5Z" />
    <path d="M5 9H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1" />
    <path d="M19 9h1a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1" />
  </svg>
);

const CalendarIcon = ({ className = '' }) => (
  <svg
    className={`h-6 w-6 ${className}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <path d="M16 2v4" />
    <path d="M8 2v4" />
    <path d="M3 10h18" />
  </svg>
);

const SettingsIcon = ({ className = '' }) => (
  <svg
    className={`h-6 w-6 ${className}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.09a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
  </svg>
);

const Header = () => {
  const { user } = useAuth();
  const { t } = useLocale();

  const navItems = [
    { type: 'link', path: '/dashboard', label: t('header.forza', 'Forza'), icon: ForzaMark },
    { type: 'link', path: '/leaderboard', label: t('header.leaderboard', 'Leaderboard'), icon: TrophyIcon },
    { type: 'link', path: '/calendar', label: t('header.calendar', 'Calendar'), icon: CalendarIcon },
    { type: 'link', path: '/settings', label: t('header.settings', 'Settings'), icon: SettingsIcon },
  ];

  const loggedOutItems = [
    { type: 'link', path: '/login', label: t('header.login', 'Login') },
    { type: 'link', path: '/register', label: t('header.register', 'Register') },
  ];

  const itemsToRender = user ? navItems : loggedOutItems;

  if (!itemsToRender || itemsToRender.length === 0) {
    return null;
  }

  return (
    <header className="py-2">
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50">
        <div
          className="pointer-events-auto border-t border-white/10 bg-slate-950/80 backdrop-blur-xl shadow-2xl"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <nav
            className="mx-auto flex w-full max-w-3xl items-stretch gap-1 px-2 sm:px-4"
            aria-label={t('header.navigation', 'Primary navigation')}
          >
            {itemsToRender.map((item) => {
              const IconComponent = item.icon;
              const isForza = item.label === 'Forza';

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `
                    relative flex-1 rounded-xl px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 border border-transparent
                    ${isActive ? 'bg-cyan-900/30 border-cyan-500/20 text-cyan-400 shadow-inner' : isForza ? 'text-cyan-500 hover:text-cyan-400 hover:bg-slate-800/50 hover:border-white/5' : 'text-gray-400 hover:text-cyan-400 hover:bg-slate-800/50 hover:border-white/5'}
                  `}
                >
                  {({ isActive }) => (
                    <>
                      {IconComponent ? (
                        <IconComponent
                          className={`mx-auto h-6 w-6 transition-colors ${isActive ? 'text-cyan-400' : isForza ? 'text-cyan-500' : 'text-gray-400'}`}
                        />
                      ) : null}
                      <span className="mt-1 block text-[11px]">{item.label}</span>
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
